import { isOnline, logger, shortUuid } from '@tunasong/models'
import { getEntityTypeFromMimeType, type TunaPlugin } from '@tunasong/plugin-lib'
import type { TunaGetState, TunaThunkDispatch } from '@tunasong/redux'
import { features } from '@tunasong/redux'
import type { Entity, Persisted } from '@tunasong/schemas'
import { openDatabase } from '@tunasong/indexeddb-storage'
import invariant from 'tiny-invariant'
import type { StoredEntityUpload } from './upload-types.js'
import { useGraphCacheStore } from '@tunasong/graph-cache'
import { blobUploadToTemporaryEntity } from './upload-util.js'

/** To guard against costly bugs that upload data ad-infinitum we set a limit on the number of files to upload per session to 1000. */
const MAX_UPLOADS_PER_SESSION = 100
// We limit the number of upload attempts to 50
const MAX_UPLOAD_ATTEMPTS = 10

// Delay between attempts (10 seconds)
const UPLOAD_RETRY_DELAY = 10000

/**
 * GH #1499: a transaction on a closing/closed IndexedDB connection throws
 * `InvalidStateError: The database connection is closing` (page teardown or a
 * `versionchange` from another tab). Used to swallow that benign case in the
 * upload loop instead of letting it surface as an error.
 */
const isDatabaseClosingError = (e: unknown): boolean => e instanceof DOMException && e.name === 'InvalidStateError'

/** Upload manager that uploads to the server asynchronously from IndexedDB */
export class UploadManager {
  #dispatch: TunaThunkDispatch
  #getState: TunaGetState
  #database: ReturnType<typeof openDatabase<StoredEntityUpload>> | null
  #uploadCount = 0
  #isInUploadLoop = false
  #uploadScheduled = false
  #pendingPromises = new Map<
    IDBValidKey,
    { resolve: (entity: Persisted<Entity>) => void; reject: (error: Error) => void } | undefined
  >()
  /** Placeholder entities this manager references in the graph cache, so eviction keeps them. GH #1643 */
  #retainedPlaceholders = new Set<IDBValidKey>()
  /** Each placeholder's blob URL, revoked once the uploaded entity replaces it. GH #2618 */
  #placeholderUrls = new Map<IDBValidKey, string>()
  #plugins: TunaPlugin[]

  constructor({
    dbName,
    dispatch,
    getState,
    pushInterval = 1000,
    plugins,
  }: {
    dbName: string
    dispatch: TunaThunkDispatch
    getState: TunaGetState
    pushInterval?: number
    plugins: TunaPlugin[]
  }) {
    this.#dispatch = dispatch
    this.#getState = getState
    this.#database = openDatabase<StoredEntityUpload>(dbName)
    this.#plugins = plugins

    // Run upload every pushInterval milliseconds.
    setInterval(this.upload, pushInterval)
  }

  get isUploading() {
    return this.#isInUploadLoop || this.#uploadScheduled
  }

  uploadNewStoredEntity = async (props: {
    entityId?: string
    name: string
    parentId: string | null
    blob: Blob
    isPrivate?: boolean
    tags?: string[]
  }) => {
    const { isPrivate = false, blob, name, parentId, tags, entityId = shortUuid() } = props
    invariant(this.#database, `No upload database`)

    if (blob.size <= 0) {
      this.#dispatch(
        features.notifications.actions.setAlert({
          severity: 'warning',
          message: `Cannot upload an empty file: ${name}`,
        })
      )
      return
    }
    if (!blob.type) {
      this.#dispatch(
        features.notifications.actions.setAlert({
          severity: 'warning',
          message: `Cannot upload a file without a mime type: ${name}`,
        })
      )
      return
    }

    if (this.#uploadCount >= MAX_UPLOADS_PER_SESSION) {
      this.#dispatch(
        features.notifications.actions.setAlert({
          severity: 'warning',
          message: `Cannot upload more than ${MAX_UPLOADS_PER_SESSION} files per session`,
        })
      )
      return
    }

    const type = getEntityTypeFromMimeType(blob.type, this.#plugins)
    if (!type) {
      this.#dispatch(
        features.notifications.actions.setAlert({
          severity: 'warning',
          message: `Cannot upload a file with an unknown mime type: ${name}`,
        })
      )
      logger.error(`Cannot upload a file with an unknown mime type: ${name}`, { mimeType: blob.type })
      return
    }

    /** Store in the pending uploads database and schedule it for upload */
    invariant(entityId, `No entityId provided`)

    // Map the blob mime type to the entity type
    const blobUpload: StoredEntityUpload = { id: entityId, name, type, parentId, blob, isPrivate, attempts: 0, tags }
    await this.#database.set(entityId, blobUpload)

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const userId = this.#getState().user.userId!

    // Update the cache optimistically, and keep the placeholder cached until the upload settles
    const { upsertPlaceholder, retain } = useGraphCacheStore.getState()
    const placeholder = blobUploadToTemporaryEntity({ blobUpload, userId })
    // Any server version of it replaces it, whatever the client clock says. GH #1913
    upsertPlaceholder(placeholder)
    if (placeholder.storageUrls?.url) {
      this.#placeholderUrls.set(entityId, placeholder.storageUrls.url)
    }
    retain(entityId)
    this.#retainedPlaceholders.add(entityId)

    const promise = new Promise<Persisted<Entity>>((resolve, reject) => {
      this.#pendingPromises.set(entityId, { resolve, reject })
    })

    this.#uploadCount += 1
    this.#uploadScheduled = true

    // trigger an upload async
    this.upload().catch(e => {
      logger.error('Error uploading', e)
    })

    return promise
  }

  // the private upload loop

  private upload = async () => {
    if (!this.#database || this.#isInUploadLoop || !isOnline()) {
      return
    }

    this.#isInUploadLoop = true

    try {
      for (const key of await this.#database.keys()) {
        const val = await this.#database.get(key)
        if (!val) {
          continue
        }

        const { id, name, parentId, blob, isPrivate, tags, type, lastAttempt } = val

        // Wait at least UPLOAD_RETRY_DELAY before retrying
        if (lastAttempt && Date.now() - lastAttempt.getTime() < UPLOAD_RETRY_DELAY) {
          await new Promise(resolve => setTimeout(resolve, UPLOAD_RETRY_DELAY))
          continue
        }

        try {
          /** We need to remove the key before uploading to avoid a race */
          await this.#database.delete(key)

          const entity = await this.#dispatch(
            features.storage.thunks.uploadStoredEntity({
              id,
              name,
              type,
              parentId,
              blob,
              message: `Uploading ${name}...`,
              tags,
              isPrivate,
            })
          ).unwrap()
          this.#releasePlaceholder(key)
          this.#revokePlaceholderUrl(key)

          const pending = this.#pendingPromises.get(key)
          if (!pending) {
            continue
          }
          pending.resolve(entity)
          this.#pendingPromises.delete(key)
        } catch (e) {
          /** Re-queue the entity on error */
          logger.warn(`Error uploading ${name}. Rescheduling upload.`, e)
          const attempts = val.attempts + 1
          if (attempts > MAX_UPLOAD_ATTEMPTS) {
            logger.error(`Failed to upload ${name} after ${MAX_UPLOAD_ATTEMPTS} attempts. Giving up.`)
            // Not every caller waits on the upload (a recording is fired and forgotten), so say it here
            this.#dispatch(
              features.notifications.actions.setAlert({
                severity: 'error',
                title: 'Upload failed',
                message: `Could not upload ${name}. Please try again.`,
              })
            )
            /** The caller is waiting on the upload; an RTK error is a plain object, so wrap it */
            this.#pendingPromises
              .get(key)
              ?.reject(new Error(`Failed to upload ${name} after ${MAX_UPLOAD_ATTEMPTS} attempts`, { cause: e }))
            this.#pendingPromises.delete(key)
            this.#releasePlaceholder(key)
            continue
          }
          void this.#database.set(key, {
            ...val,
            lastAttempt: new Date(),
            attempts,
          })
        }
      }
    } catch (e) {
      /**
       * GH #1499: the queue reads above (`keys()` / `get()`) run transactions on
       * the shared IndexedDB connection. When that connection is closing — page
       * teardown, or a `versionchange` from another tab — they throw
       * `InvalidStateError: The database connection is closing`. That is benign
       * (nothing to upload against a closing connection): swallow it so it does
       * not surface as an error, and let `finally` reset the loop flag so a
       * transient close cannot wedge uploads for the rest of the session.
       */
      if (isDatabaseClosingError(e)) {
        logger.debug('Upload loop aborted: IndexedDB connection is closing', e)
      } else {
        logger.warn('Upload loop failed unexpectedly', e)
      }
    } finally {
      this.#isInUploadLoop = false
      this.#uploadScheduled = false
    }
  }

  #releasePlaceholder = (key: IDBValidKey) => {
    if (typeof key !== 'string' || !this.#retainedPlaceholders.delete(key)) {
      return
    }
    useGraphCacheStore.getState().release(key)
  }

  /**
   * The uploaded entity has replaced the placeholder in the cache, so nothing shows its blob URL any more. A
   * given-up upload keeps its URL: its placeholder is still shown. GH #2618
   */
  #revokePlaceholderUrl = (key: IDBValidKey) => {
    const url = this.#placeholderUrls.get(key)
    if (!url) {
      return
    }
    this.#placeholderUrls.delete(key)
    URL.revokeObjectURL(url)
  }
}
